import * as React from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { MinusIcon } from "lucide-react";

import { cn } from "@/lib/utils";

interface InputOTPProps extends React.ComponentProps<typeof OTPInput> {
    containerClassName?: string;
    hasError?: boolean;
    onComplete?: (value: string) => void;
    errorMessage?: string;
    onChange?: (value: string) => void;
    maxLength?: number; 
    className?: string;
    loading?: boolean;
}

function InputOTP({
    className,
    containerClassName,
    hasError,
    onComplete,
    onChange,
    maxLength,
    errorMessage,
    loading,
    ...props
}: InputOTPProps) {
    const handleChange = (value: string) => {
        if (onChange) {
            onChange(value);
        }
        if (value.length === maxLength && onComplete) {
            onComplete(value);
        }
    };

    return (
        <div className="relative">
            <div className="relative flex items-center">
                <OTPInput
                    data-slot="input-otp"
                    containerClassName={cn(
                        "flex items-center gap-3 has-disabled:opacity-50",
                        loading && "opacity-50 pointer-events-none",
                        containerClassName
                    )}
                    className={cn("disabled:cursor-not-allowed", className)}
                    onChange={handleChange}
                    maxLength={maxLength}
                    {...props}
                >
                    <InputOTPGroup>
                        {Array.from({ length: maxLength || 6 }).map((_, index) => (
                            <InputOTPSlot key={index} index={index} hasError={hasError} />
                        ))}
                    </InputOTPGroup>
                </OTPInput>
                {loading && (
                    <div className="ml-4">
                        <div className="size-4 border-2 border-primary border-t-transparent rounded-full animate-spin"/>
                    </div>
                )}
            </div>
            {hasError && errorMessage && (
                <p className="text-[14px] text-sm text-red-500 pt-3 whitespace-normal font-normal tracking-[0.07px]">
                    {errorMessage}
                </p>
            )}
        </div>
    );
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="input-otp-group"
            className={cn("flex items-center gap-3", className)}
            {...props}
        />
    );
}

interface InputOTPSlotProps extends React.ComponentProps<"div"> {
    index: number;
    hasError?: boolean;
}

function InputOTPSlot({
    index,
    className,
    hasError,
    ...props
}: InputOTPSlotProps) {
    const inputOTPContext = React.useContext(OTPInputContext);
    const { char, isActive } = inputOTPContext?.slots[index] ?? {};
    const displayChar = char || "-";

    return (
        <div
            data-slot="input-otp-slot"
            data-active={isActive}
            className={cn(
                "border-input file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground",
                "flex h-12 w-10 items-center justify-center text-base bg-[var(--input-background)] shadow-xs transition-all outline-none",
                "rounded-md border",
                "disabled:cursor-not-allowed disabled:opacity-50",
                isActive && !hasError && "border-transparent ring-[var(--input-highlight-border)] ring-[0.25px] text-primary",
                hasError && "border-red-500 focus:border-red-500 focus:ring-red-500 ring-red-500 ring-[0.25px]",
                !isActive && !hasError && "border-input",
                className
            )}
            {...props}
        >
            {displayChar}
        </div>
    );
}

function InputOTPSeparator({ ...props }: React.ComponentProps<"div">) {
    return (
        <div data-slot="input-otp-separator" role="separator" className="text-gray-500" {...props}>
            <MinusIcon />
        </div>
    );
}

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
